import React, { useEffect, useState } from 'react';
import {
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  Blocks,
  BriefcaseBusiness,
  Download,
  Linkedin,
  Mail,
  Menu,
  Moon,
  Network,
  ShieldCheck,
  Sun,
  X,
} from 'lucide-react';
import './TailwindProfile.css';
import profilePic from './assets/images/profile_pic.png';

const proofPoints = [
  { value: '50+', label: 'operational dashboards' },
  { value: '200+', label: 'enterprise domains' },
  { value: '8,000', label: 'applications mapped' },
  { value: '4', label: 'AI providers integrated' },
];

const capabilities = [
  {
    icon: <BriefcaseBusiness aria-hidden="true" />,
    title: 'Discover & align',
    text: 'Translate business objectives, operational constraints, and stakeholder needs into clear solution scope and decisions.',
    tags: ['Business discovery', 'Stakeholder facilitation', 'Solution analysis'],
  },
  {
    icon: <Network aria-hidden="true" />,
    title: 'Architect & govern',
    text: 'Shape event-driven integrations and govern the contracts, controls, and non-functional requirements behind them.',
    tags: ['Solution architecture', 'API/data governance', 'Event-driven integration'],
  },
  {
    icon: <ShieldCheck aria-hidden="true" />,
    title: 'Deliver & assure',
    text: 'Lead technical change from investigation and design through testing, production readiness, rollout, and learning.',
    tags: ['Change governance', 'Production readiness', 'Operational risk'],
  },
];

const technologyGroups = [
  {
    label: 'Integration',
    values: ['Solace PubSub+', 'OpenAPI', 'AsyncAPI', 'XML / XSD'],
  },
  {
    label: 'Platform',
    values: ['Azure', 'Kubernetes', 'OpenShift', 'CI/CD', 'Splunk'],
  },
  {
    label: 'Engineering',
    values: ['Java / Spring Boot', 'Python', 'JavaScript / React', 'SQL / NoSQL', 'Linux'],
  },
];

function App() {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const savedTheme = window.localStorage.getItem('jack-portfolio-theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setIsDarkMode(savedTheme ? savedTheme === 'dark' : prefersDark);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = isDarkMode ? 'dark' : 'light';
    window.localStorage.setItem('jack-portfolio-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="site-shell">
      <a className="skip-link" href="#main">Skip to content</a>

      <header className="site-header">
        <a className="wordmark" href="#top" aria-label="Jack Wong, home" onClick={closeMenu}>
          <span className="wordmark-mark">JW</span>
          <span className="wordmark-copy">
            <strong>Jack Wong</strong>
            <small>Enterprise technology</small>
          </span>
        </a>

        <nav className={menuOpen ? 'site-nav is-open' : 'site-nav'} aria-label="Primary navigation">
          <a href="#impact" onClick={closeMenu}>Impact</a>
          <a href="#experience" onClick={closeMenu}>Experience</a>
          <a href="#capabilities" onClick={closeMenu}>Capabilities</a>
          <a href="#contact" onClick={closeMenu}>Contact</a>
        </nav>

        <div className="header-actions">
          <button
            className="icon-button"
            type="button"
            aria-label={isDarkMode ? 'Switch to light theme' : 'Switch to dark theme'}
            onClick={() => setIsDarkMode((current) => !current)}
          >
            {isDarkMode ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
          </button>
          <a className="header-resume" href="/Wong_Chung_Pak_Jack_Resume.pdf" download>
            Résumé <Download aria-hidden="true" />
          </a>
          <button
            className="menu-button"
            type="button"
            aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((current) => !current)}
          >
            {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
        </div>
      </header>

      <main id="main">
        <section className="hero" id="top">
          <div className="hero-copy">
            <div className="availability"><span /> Hong Kong · Exploring 2026 opportunities</div>
            <p className="eyebrow">Technical Manager · Solution Architecture</p>
            <h1>Bridging business priorities and technology into <em>governed, production-ready solutions.</em></h1>
            <p className="hero-summary">
              Business-facing Technical Manager with experience across enterprise integration,
              event-driven platforms, API/data governance, operational intelligence, and AI-enabled platforms.
            </p>
            <div className="hero-actions">
              <a className="primary-button" href="#impact">
                View selected impact <ArrowDownRight aria-hidden="true" />
              </a>
              <a className="text-link" href="mailto:chungpakjack.wong@outlook.com">
                Start a conversation <ArrowUpRight aria-hidden="true" />
              </a>
            </div>
          </div>

          <div className="portrait-stage" aria-label="Portrait of Jack Wong">
            <div className="portrait-frame">
              <img src={profilePic} alt="Jack Wong" />
            </div>
          </div>
        </section>

        <section className="proof-strip" aria-label="Selected scale and impact">
          {proofPoints.map((item) => (
            <div className="proof-point" key={item.label}>
              <strong>{item.value}</strong>
              <span>{item.label}</span>
            </div>
          ))}
        </section>

        <section className="section-block impact-section" id="impact">
          <div className="section-intro">
            <p className="eyebrow">Selected impact</p>
            <h2>Architecture decisions grounded in operational reality.</h2>
            <p>
              I work across business requirements, interface contracts, platform behaviour, and production operations—so decisions remain useful after the diagram is finished.
            </p>
          </div>

          <div className="impact-grid">
            <article className="impact-card impact-feature">
              <div className="card-icon"><Network aria-hidden="true" /></div>
              <span className="card-kicker">Business-critical integration</span>
              <h3>Governed the contracts behind a low-latency enterprise platform.</h3>
              <p>
                Lead solution analysis and interface governance across inbound processing and request/reply,
                publish/subscribe, and push services, guiding delivery teams and consultants toward production-ready decisions.
              </p>
              <div className="tag-row">
                <span>OpenAPI</span><span>AsyncAPI</span><span>XSD</span><span>Solace PubSub+</span>
              </div>
            </article>

            <article className="impact-card">
              <div className="card-icon"><BarChart3 aria-hidden="true" /></div>
              <span className="card-kicker">Operational intelligence</span>
              <h3>Made distributed-system behaviour visible.</h3>
              <p>
                Created 50+ Splunk dashboards covering throughput, latency, failures, capacity, and client health across the processing journey.
              </p>
            </article>

            <article className="impact-card">
              <div className="card-icon"><Blocks aria-hidden="true" /></div>
              <span className="card-kicker">Interface enablement</span>
              <h3>Turned specifications into searchable knowledge.</h3>
              <p>
                Built an IDA data-dictionary prototype exposing endpoint trees, field paths, data types, cardinality, and source mappings for faster analysis.
              </p>
            </article>

            <article className="impact-card impact-wide">
              <div>
                <span className="card-kicker">Enterprise automation</span>
                <h3>Converted fragmented configuration into a usable enterprise view.</h3>
              </div>
              <p>
                At Hospital Authority, designed an inventory and self-service automation platform spanning 200+ domains and 8,000 applications—turning configuration data into dashboards and actionable controls.
              </p>
            </article>
          </div>
        </section>

        <section className="section-block experience-section" id="experience">
          <div className="section-intro compact-intro">
            <p className="eyebrow">Experience</p>
            <h2>From hands-on delivery to broader technical ownership.</h2>
          </div>

          <div className="timeline">
            <article className="timeline-entry">
              <div className="timeline-meta">
                <span className="timeline-year">2024 — now</span>
                <span className="timeline-dot" />
              </div>
              <div className="timeline-content">
                <div className="role-heading">
                  <div>
                    <h3>The Hong Kong Jockey Club</h3>
                    <p>Assistant Technical Manager — Technology Office</p>
                  </div>
                  <span>Promoted Sep 2025</span>
                </div>
                <p>
                  Own solution analysis, interface governance, production readiness, and operational intelligence for a business-critical integration platform supporting racing and wagering operations.
                </p>
                <ul>
                  <li>Coordinate business, architecture, engineering, operations, downstream teams, and consultants.</li>
                  <li>Lead incident resolution from analysis through testing, rollout, and stronger service and contract controls.</li>
                  <li>Translate external-consumption needs into service levels, access rules, permissions, and restricted-data controls.</li>
                </ul>
              </div>
            </article>

            <article className="timeline-entry">
              <div className="timeline-meta">
                <span className="timeline-year">2022 — 2024</span>
                <span className="timeline-dot" />
              </div>
              <div className="timeline-content">
                <div className="role-heading">
                  <div>
                    <h3>Hospital Authority</h3>
                    <p>Programmer · Enterprise Platforms</p>
                  </div>
                </div>
                <p>
                  Coordinated product teams and external healthcare providers to deliver cloud-based self-service, data-access, and cross-boundary health-record solutions.
                </p>
                <ul>
                  <li>Owned the rostering solution lifecycle from requirements and architecture through rollout planning and adoption.</li>
                  <li>Introduced automated verification across multiple products.</li>
                </ul>
              </div>
            </article>
          </div>
        </section>

        <section className="case-study" id="ai-project" aria-labelledby="ai-platform-title">
          <div className="case-study-copy">
            <p className="eyebrow">Independent architecture project · 2026</p>
            <h2 id="ai-platform-title">Personal AI Workspace & Delivery Control Platform</h2>
            <p>
              A private multi-agent workspace that brings four AI providers into one governed environment for chat and engineering workflows, with evidence capture, human review, and an auditable path from finding to approved release.
            </p>
            <div className="case-principles">
              <span>Multi-model orchestration</span>
              <span>Human-in-the-loop</span>
              <span>Auditable workflow</span>
            </div>
          </div>
          <div className="architecture-card">
            <div className="architecture-header">
              <Network aria-hidden="true" />
              <span>Governed AI delivery workflow</span>
            </div>
            <div className="workflow-stack">
              <div className="workflow-stage">
                <small>01 · Unified workspace</small>
                <strong>Four AI providers</strong>
                <p>One interface for chat and engineering workflows</p>
              </div>
              <div className="workflow-connector">
                <span>Context, routing & comparison</span>
              </div>
              <div className="workflow-stage workflow-stage-emphasis">
                <small>02 · Human control</small>
                <strong>Review checkpoints</strong>
                <p>Evidence, permissions, and accountable decisions</p>
              </div>
              <div className="workflow-connector">
                <span>Auditable hand-off</span>
              </div>
              <div className="workflow-stage">
                <small>03 · Delivery outcome</small>
                <strong>Approved release</strong>
                <p>Findings translated into controlled change</p>
              </div>
            </div>
            <div className="architecture-footer">
              <span>Multi-model</span><span>Traceable</span><span>Production-aware</span>
            </div>
          </div>
        </section>

        <section className="section-block capability-section" id="capabilities">
          <div className="section-intro">
            <p className="eyebrow">How I work</p>
            <h2>Enough engineering depth to make architecture executable.</h2>
          </div>

          <div className="capability-grid">
            {capabilities.map((capability) => (
              <article className="capability-card" key={capability.title}>
                <div className="capability-icon">{capability.icon}</div>
                <h3>{capability.title}</h3>
                <p>{capability.text}</p>
                <ul>
                  {capability.tags.map((tag) => <li key={tag}>{tag}</li>)}
                </ul>
              </article>
            ))}
          </div>

          <div className="technology-panel">
            <div>
              <p className="eyebrow">Technology foundation</p>
              <h3>Architecture-led. Technically grounded.</h3>
            </div>
            <div className="technology-list">
              {technologyGroups.map((group) => (
                <div className="technology-group" key={group.label}>
                  <strong>{group.label}</strong>
                  <p>{group.values.join(' · ')}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="contact-section" id="contact">
          <div>
            <p className="eyebrow">Let’s work together</p>
            <h2>Looking for someone who can connect the system, the decision, and the outcome?</h2>
          </div>
          <div className="contact-actions">
            <a className="primary-button light-button" href="mailto:chungpakjack.wong@outlook.com">
              Email Jack <Mail aria-hidden="true" />
            </a>
            <a className="text-link light-link" href="https://www.linkedin.com/in/chungpakjackwong/" target="_blank" rel="noreferrer">
              LinkedIn <Linkedin aria-hidden="true" />
            </a>
          </div>
        </section>
      </main>

      <footer>
        <span>Jack Wong · Hong Kong</span>
        <span>Technical leadership · Solution architecture · Enterprise integration</span>
      </footer>
    </div>
  );
}

export default App;
